import {
  incidentProfiles,
  profileIds,
  defaultProfiles,
  type ProfileId,
} from "../domain/incidentProfiles";
import { useEffect, useRef, useState } from "react";
import { PhoneIncoming, Shuffle, MapPin, Search } from "lucide-react";
import { templates } from "../domain/scenarios";
import {
  type Incident,
  type Point,
  type SimState,
  incidentLabels,
  IncidentType,
} from "../domain/model";
import type { ResolvedAddress } from "../domain/addresses";
import { distanceKm } from "../providers/routing";
import { request } from "../api";
import type { Perform } from "./ResourceManager";

export default function CallComposer({
  act,
  enabled,
  onCreated,
  state,
  initialPoint,
}: {
  act: Perform;
  enabled: boolean;
  onCreated: (id: string, point: Point) => void;
  state: SimState;
  initialPoint?: Point | null;
}) {
  const [type, setType] = useState<Incident["type"]>("brand");
  const [profileId, setProfileId] = useState<ProfileId>("brand-wohnung");
  const [mode, setMode] = useState(initialPoint ? "custom" : "region");
  const [report, setReport] = useState(templates.brand.report);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ResolvedAddress[]>([]);
  const [site, setSite] = useState<ResolvedAddress | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const sequence = useRef(0);
  const areaId = state.area.controlCenterId ?? state.area.name;

  async function lookup(kind: "reverse" | "search") {
    const current = ++sequence.current;
    setLoading(true);
    setError("");
    setSite(null);
    setResults([]);
    const path =
      kind === "reverse" && initialPoint
        ? "geocoding/reverse?lat=" + initialPoint[0] + "&lng=" + initialPoint[1]
        : "geocoding/search?q=" + encodeURIComponent(query.trim());
    try {
      const data = await request<{ sites: ResolvedAddress[] }>(path);
      if (current !== sequence.current) return;
      setResults(data.sites);
      if (kind === "reverse" && data.sites[0]) setSite(data.sites[0]);
      if (!data.sites.length)
        setError(
          "Keine passende Straße oder Hausnummer im Kartenbestand gefunden. Bitte Straße, Hausnummer und Ort suchen oder einen anderen Kartenpunkt wählen.",
        );
    } catch (e) {
      if (current === sequence.current) setError((e as Error).message);
    } finally {
      if (current === sequence.current) setLoading(false);
    }
  }
  useEffect(() => {
    setSite(null);
    setResults([]);
    setError("");
    setLoading(false);
    if (mode === "custom" && initialPoint) void lookup("reverse");
    return () => {
      sequence.current++;
    };
  }, [mode, initialPoint?.[0], initialPoint?.[1], areaId]);

  async function create(random = false) {
    if (loading || !enabled || (!random && mode === "custom" && !site)) return;
    const result = await act(
      {
        type: "createIncident",
        ...(!random
          ? {
              incidentType: type,
              profileId,
              report,
              siteId: mode === "custom" ? site!.addressSource.id : undefined,
            }
          : {}),
      },
      "Notruf mit Adresse aus dem Kartenbestand aufgenommen.",
    );
    if (result) onCreated(result.incidents[0].id, result.incidents[0].location);
  }
  return (
    <form
      className="dialog-stack"
      onSubmit={(e) => {
        e.preventDefault();
        void create();
      }}
    >
      <label>
        Einsatzart
        <select
          aria-label="Einsatzart"
          value={type}
          onChange={(e) => {
            const next = e.target.value as Incident["type"];
            setType(next);
            setProfileId(defaultProfiles[next]);
            setReport(templates[next].report);
          }}
        >
          {IncidentType.options.map((t) => (
            <option key={t} value={t}>
              {incidentLabels[t].code} · {incidentLabels[t].title}
            </option>
          ))}
        </select>
      </label>
      <label>
        Meldungsbild / Kräftebedarf
        <select
          aria-label="Einsatzprofil"
          value={profileId}
          onChange={(e) => {
            const id = e.target.value as ProfileId;
            setProfileId(id);
            setReport(incidentProfiles[id].report);
          }}
        >
          {profileIds
            .filter((id) => incidentProfiles[id].type === type)
            .map((id) => (
              <option value={id} key={id}>
                {incidentProfiles[id].title}
              </option>
            ))}
        </select>
      </label>
      <label>
        Einsatzort
        <select
          aria-label="Ortsauswahl"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <option value="region">
            Echte Adresse im Einsatzgebiet · {state.area.name}
          </option>
          <option value="custom">Adresse suchen / Kartenpunkt</option>
        </select>
      </label>
      {mode === "custom" ? (
        <>
          <label>
            Adresse suchen
            <div className="address-search-row">
              <input
                aria-label="Adresse suchen"
                value={query}
                maxLength={160}
                placeholder="Straße, Hausnummer, Ort – z. B. Blumenstraße 15, Bönnigheim"
                onChange={(e) => {
                  sequence.current++;
                  setLoading(false);
                  setQuery(e.target.value);
                  setSite(null);
                  setResults([]);
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (query.trim().length >= 3) void lookup("search");
                  }
                }}
              />
              <button
                type="button"
                className="button secondary"
                disabled={loading || query.trim().length < 3}
                onClick={() => void lookup("search")}
              >
                <Search size={16} />
                Suchen
              </button>
            </div>
          </label>
          {initialPoint && (
            <p className="muted small">
              <MapPin size={13} /> Kartenpunkt {initialPoint[0].toFixed(6)},{" "}
              {initialPoint[1].toFixed(6)}. Die nächstgelegene hinterlegte
              Adresse wird vorgeschlagen. Bitte die Hausnummer prüfen.{" "}
              <button
                type="button"
                className="text-button"
                disabled={loading}
                onClick={() => void lookup("reverse")}
              >
                Kartenadresse erneut laden
              </button>
            </p>
          )}
          {loading && (
            <p role="status">Adresse wird im Kartenbestand gesucht …</p>
          )}
          {!!results.length && (
            <fieldset className="address-results">
              <legend>Adresse übernehmen</legend>
              {results.map((item) => (
                <label key={item.addressSource.id} className="address-result">
                  <input
                    type="radio"
                    name="resolved-address"
                    checked={site?.addressSource.id === item.addressSource.id}
                    onChange={() => setSite(item)}
                  />
                  <span>
                    <strong>{item.address}</strong>
                    <small>
                      {item.district}
                      {initialPoint
                        ? " · " +
                          Math.round(
                            distanceKm(initialPoint, item.location) * 1000,
                          ) +
                          " m vom Kartenpunkt"
                        : ""}
                    </small>
                  </span>
                </label>
              ))}
            </fieldset>
          )}
          {site && (
            <div
              className="address-preview"
              aria-label="Bestätigte Einsatzadresse"
            >
              <strong>
                <MapPin size={16} />
                {site.address}
              </strong>
              <span>{site.district}</span>
              <small>
                {site.location[0].toFixed(6)}, {site.location[1].toFixed(6)} ·
                wird als Einsatzposition übernommen
              </small>
              {site.addressSource.accuracy === "street" && (
                <p>
                  Keine Hausnummer hinterlegt. Übernommen wird nur die Straße,
                  ohne erfundene Nummer.
                </p>
              )}
              <a
                href={site.addressSource.sourceUrl}
                target="_blank"
                rel="noreferrer"
              >
                Adresse in OpenStreetMap prüfen
              </a>
            </div>
          )}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
        </>
      ) : (
        <p className="muted small" role="status">
          Der Generator wählt eine hinterlegte Straße mit Hausnummer innerhalb
          deines Einsatzgebiets.
          {state.addressError
            ? " " + state.addressError
            : !state.addressPool?.length
              ? " Die ersten Adressen werden geladen."
              : ""}
        </p>
      )}
      <label>
        Meldungsbild
        <textarea
          rows={5}
          minLength={5}
          maxLength={1500}
          required
          value={report}
          onChange={(e) => setReport(e.target.value)}
        />
      </label>
      <p className="muted small">
        Adressdaten © OpenStreetMap-Mitwirkende, Suche über Photon. Notruf,
        Anrufername und Einsatzlage sind fiktiv.
      </p>
      <button
        className="button primary"
        disabled={!enabled || loading || (mode === "custom" && !site)}
      >
        <PhoneIncoming size={17} />
        Notruf aufnehmen
      </button>
      <button
        type="button"
        className="text-button"
        disabled={!enabled || loading}
        onClick={() => void create(true)}
      >
        <Shuffle size={14} />
        Zufälligen Notruf mit echter Adresse erzeugen
      </button>
    </form>
  );
}
